import React, { FC, useCallback, useMemo } from 'react';
import { Button, ButtonIcon, Tooltip } from '@cubeyou-team/hx-react-components';
import { useDispatch, useSelector } from 'react-redux';

import { getTargets } from 'src/selectors/queryBuilder';
import { copyTarget } from 'src/actions/QueryActions';
import { showModal } from 'src/actions/ModalActions';
import { isEmpty } from 'src/utils';
import { TRIAL_FEATURES, useTrialPaywall } from 'src/modules/trial-paywall';

interface CopyAudienceButtonProps {
  target: any;
  isQueryLoading: boolean;
  isStaleyQueryEnabled?: boolean;
}

export const CopyAudienceButton: FC<CopyAudienceButtonProps> = ({
  target,
  isQueryLoading,
  isStaleyQueryEnabled,
}) => {
  const dispatch = useDispatch();
  const targets = useSelector(getTargets);
  const { guardFeature, isTrial } = useTrialPaywall();

  const isCopyEnabled = useMemo(
    () =>
      !isEmpty(target?.target_query?.arguments_list) &&
      !target?.target_query?.arguments_list[0]?.id?.includes('tmp_'),
    [target]
  );
  const handleCopy = useCallback(() => {
    guardFeature(TRIAL_FEATURES.QUERY_BUILDER_COPY_AUDIENCE, () => {
      if (targets.length === 1) {
        dispatch(copyTarget(target.target_id, '-1'));
        return;
      }

      const targetIndex = targets.findIndex(
        (t) => t.target_id === target.target_id
      );
      dispatch(
        showModal(
          {
            audience: {
              ...target,
              index: targetIndex,
            },
            isOpen: true,
            position: 'center',
          },
          'CopyAudience'
        )
      );
    });
  }, [dispatch, guardFeature, targets, target]);

  const props = {
    icon: 'copy',
    size: 'small',
    variant: isStaleyQueryEnabled ? 'subtle' : 'outlined',
    type: 'button',
    'data-test-id': 'copy-audience',
    disabled: isQueryLoading || (!isTrial && !isCopyEnabled),
    onClick: handleCopy,
  };

  const copyButton = isStaleyQueryEnabled ? (
    <ButtonIcon {...props} />
  ) : (
    <Button {...props}>Copy</Button>
  );

  return (
    <Tooltip content="Copy audience" position="bottom" isInfoLabel>
      {copyButton}
    </Tooltip>
  );
};
